/**
 * Audit-Trail Query-API-Client (Spec 528, Phase 6).
 *
 * Ruft die verdichtete Plattform-Route `GET /v1/admin/audit-events` auf. Der
 * Aufrufer waehlt GENAU EINE Abfrage-Dimension (Backend antwortet sonst 400);
 * Zusatzfilter (action/result) und Paginierung (limit/cursor) sind optional.
 */

import { get } from './client';
import type { AuditEvent, AuditEventsResponse, AuditQueryParams } from '@/types/audit';

const PATH = '/admin/audit-events';

/**
 * Sicherheits-Deckel fuer den vollstaendigen Export: bricht die Cursor-Verfolgung
 * ab, damit ein fehlerhafter/endloser Cursor nicht in eine Endlosschleife/OOM
 * laeuft. Bei 50–200 Events/Tag deckt das 25–100 Tage einer Ein-Dimensions-Abfrage.
 */
export const EXPORT_EVENT_CAP = 5000;

/** Baut die Query-Parameter aus den gewaehlten Dimensions-/Filter-Werten. */
export function buildAuditQueryParams(query: AuditQueryParams): Record<string, string> {
  const params: Record<string, string> = {};
  switch (query.mode) {
    case 'actor':
      if (query.actorId) params.actorId = query.actorId;
      break;
    case 'resource':
      if (query.resourceType) params.resourceType = query.resourceType;
      if (query.resourceId) params.resourceId = query.resourceId;
      break;
    case 'date':
      if (query.date) params.date = query.date;
      break;
    case 'range':
      if (query.from) params.from = query.from;
      if (query.to) params.to = query.to;
      break;
  }
  if (query.filters?.action) params.action = query.filters.action;
  if (query.filters?.result) params.result = query.filters.result;
  if (query.limit) params.limit = String(query.limit);
  if (query.cursor) params.cursor = query.cursor;
  // Backend-Export-Modus (Audit-des-Audits: `audit.export`). Separater Param neben
  // der Abfrage-Dimension.
  if (query.exportEvents) params.mode = 'export';
  return params;
}

/**
 * True, wenn die gewaehlte Dimension vollstaendig gefuellt ist (Query darf laufen).
 * Verhindert einen 400-Roundtrip bei halb-ausgefuellten Filtern.
 */
export function isAuditQueryComplete(query: AuditQueryParams): boolean {
  switch (query.mode) {
    case 'actor':
      return Boolean(query.actorId);
    case 'resource':
      return Boolean(query.resourceType && query.resourceId);
    case 'date':
      return Boolean(query.date);
    case 'range':
      return Boolean(query.from && query.to);
    default:
      return false;
  }
}

export async function listAuditEvents(query: AuditQueryParams): Promise<AuditEventsResponse> {
  const raw = await get<AuditEventsResponse>(PATH, buildAuditQueryParams(query));
  return { events: raw.events ?? [], cursor: raw.cursor ?? null };
}

/**
 * Export-Abfrage (platform_admin): identische Dimension wie die Liste, aber im
 * Backend-Export-Modus — protokolliert `audit.export` (Audit-des-Audits).
 */
export async function exportAuditEvents(query: AuditQueryParams): Promise<AuditEventsResponse> {
  return listAuditEvents({ ...query, exportEvents: true });
}

/**
 * Vollstaendiger Export: verfolgt den `cursor` bis zum Ende, damit der Beleg
 * ALLE Events der abgefragten Dimension/Filter enthaelt (nicht nur die erste
 * Seite). **Nur der erste Seiten-Fetch** laeuft im Export-Modus
 * (`exportEvents:true`) → genau EIN `audit.export`-Ereignis pro Nutzeraktion;
 * die Folge-Seiten nutzen das schlichte Listing. Bei erreichtem Sicherheits-
 * Deckel bricht die Verfolgung ab und `truncated` wird gesetzt (die UI/CSV legt
 * das offen). Ein Fehler mitten in der Paginierung verwirft den Teil-Export und
 * wirft weiter — es entsteht kein stiller „halber Beleg".
 */
export async function fetchAllAuditEvents(
  query: AuditQueryParams,
  cap: number = EXPORT_EVENT_CAP,
): Promise<{ events: AuditEvent[]; truncated: boolean }> {
  const events: AuditEvent[] = [];
  let cursor: string | undefined;
  let first = true;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const page: AuditEventsResponse = await listAuditEvents({
      ...query,
      exportEvents: first,
      cursor,
    });
    first = false;
    events.push(...page.events);

    if (!page.cursor) {
      // Terminale Seite: vollstaendig — es sei denn, DIESE Seite hat den Deckel
      // ueberlaufen (dann wird geschnitten und als unvollstaendig markiert).
      return events.length > cap
        ? { events: events.slice(0, cap), truncated: true }
        : { events, truncated: false };
    }
    if (events.length >= cap) {
      // Weitere Seiten vorhanden, aber der Deckel ist erreicht.
      return { events: events.slice(0, cap), truncated: true };
    }
    cursor = page.cursor;
  }
}
